<template>
  <article
    class="max-root"
    :class="{ 'max-motion': !reducedMotion }"
    :style="rootStyle"
    :lang="lang"
    :data-max-bleed="bleed ? 'true' : undefined"
    :data-max-scheme="scheme"
  >
    <div v-if="!sanitized.doc" class="max-degraded" role="alert">{{ labels.degraded }}</div>
    <template v-else>
      <header v-if="!hideHeader" class="max-doc__header" data-max-motion="rise">
        <div class="max-doc__top">
          <h1 class="max-doc__title">{{ sanitized.doc.title }}</h1>
          <div v-if="$slots.actions" class="max-doc__actions"><slot name="actions" /></div>
        </div>
        <p v-if="sanitized.doc.subtitle" class="max-doc__subtitle">{{ sanitized.doc.subtitle }}</p>
        <p v-if="generatedLabel || periodLabel || $slots.meta" class="max-doc__meta">
          <span v-if="generatedLabel">{{ labels.generated }} {{ generatedLabel }}</span>
          <span v-if="generatedLabel && periodLabel" aria-hidden="true">·</span>
          <span v-if="periodLabel">{{ labels.period }} {{ periodLabel }}</span>
          <slot name="meta" />
        </p>
      </header>
      <slot name="notice" />
      <div class="max-doc__body">
        <MaxNode
          v-for="(node, i) in sanitized.doc.body"
          :key="(node as any).id ?? i"
          :node="node"
          :index="i"
        />
      </div>
    </template>
  </article>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { sanitizeDocument } from '../core/document.ts'
import { presetCssVars, getPreset } from '../core/tokens.ts'
import { mergeLabels, type MaxLabels } from '../core/labels.ts'
import { toBcp47 } from '../core/format.ts'
import { provideMaxContext, useMaxPreset, useReducedMotion, type MaxRenderContext } from '../runtime/context.ts'

const props = withDefaults(defineProps<{
  doc: unknown
  labels?: Partial<MaxLabels> | null
  /** Overrides doc.preset, e.g. to force the dense look inside a dashboard tile */
  preset?: string | null
  bleed?: boolean
  hideHeader?: boolean
  openEntity?: MaxRenderContext['openEntity']
  canOpenEntity?: MaxRenderContext['canOpenEntity']
}>(), {
  labels: null,
  preset: null,
  bleed: false,
  hideHeader: false,
  openEntity: undefined,
  canOpenEntity: undefined
})

const sanitized = computed(() => sanitizeDocument(props.doc))
const labels = computed(() => mergeLabels(props.labels))
const presetId = useMaxPreset(() => props.preset, () => sanitized.value.doc?.preset)
const locale = computed(() => sanitized.value.doc?.locale ?? 'en_US')
const lang = computed(() => toBcp47(locale.value))
const scheme = computed(() => getPreset(presetId.value).scheme)
const rootStyle = computed(() => presetCssVars(presetId.value))
const reducedMotion = useReducedMotion()

const generatedLabel = computed(() => {
  const at = sanitized.value.doc?.generatedAt
  if (!at) return ''
  const date = new Date(at)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(lang.value, { dateStyle: 'long', timeStyle: at.includes('T') ? 'short' : undefined }).format(date)
})

const periodLabel = computed(() => {
  const period = sanitized.value.doc?.period
  if (!period) return ''
  if (period.label) return period.label
  return [period.from, period.to].filter(Boolean).join(' – ')
})

provideMaxContext({
  get labels () { return labels.value },
  get locale () { return locale.value },
  get preset () { return presetId.value },
  reducedMotion: toRef(reducedMotion),
  get openEntity () { return props.openEntity },
  get canOpenEntity () { return props.canOpenEntity }
} as MaxRenderContext)
</script>
